/**
 * FloatingLiveCallCoach — Phase 3.1 (AI Call Intelligence)
 *
 * A global, route-agnostic floating panel that wraps the existing
 * LiveCoachPanel (the WebRTC mic-capture + Whisper + GPT-5-mini sales
 * coach used inside /dialer) and surfaces it on EVERY route the rep
 * navigates while a live call is in progress.
 *
 * Activation rules:
 *   • Listens for `webphone:call-started` / `webphone:call-ended`
 *     events broadcast by `RCWebPhone` (the persistent SIP softphone
 *     mounted inside GlobalWebPhoneDock).
 *   • On call-started, fetches the lead by `lead_id` (if provided in
 *     the event) so the coach prompt is grounded in the right context.
 *   • On call-ended, gracefully tears down mic capture + clears state.
 *
 * Non-interference contract (Josh, Iter 158):
 *   • Never imports RCWebPhone or GlobalWebPhoneDock.
 *   • Never dispatches webphone:* events.
 *   • Pure consumer of the existing event bus.
 *   • Skips itself on /dialer (which already mounts LiveCoachPanel
 *     inline) and /communications (inline SIP registration page).
 *   • Minimizable + draggable — never blocks the dock or interferes
 *     with the call audio path.
 */
import React, { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { Sparkles, X, Minus, Move, Phone } from "lucide-react";
import LiveCoachPanel from "@/components/dialer/LiveCoachPanel";
// Iter 346 — shared bottom-right lane geometry. clampFloaterPos keeps
// this widget out of the reserved bottom-dock strip so it can never
// overlap the AI-FAB / NotificationBell / RC pill / Live Notes panel.
import {
  FLOATER_MIN_BOTTOM,
  FLOATER_MIN_RIGHT,
  clampFloaterPos,
  defaultFloaterPos,
} from "@/lib/floatingLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => {
  const t = localStorage.getItem("access_token") || localStorage.getItem("token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const POS_KEY = "rc.live-coach.pos";
const COLLAPSED_KEY = "rc.live-coach.collapsed";

// Routes that already mount LiveCoachPanel inline. Avoid double-mount
// so we never spawn two MediaRecorders against the same mic stream.
const SKIP_ROUTES = ["/dialer", "/communications"];

function loadPos() {
  try {
    const raw = sessionStorage.getItem(POS_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw);
    if (typeof v?.right === "number" && typeof v?.bottom === "number") {
      // Iter 346 — clamp any saved position so the widget can't sit inside
      // the reserved bottom-dock strip (which was the actual cause of the
      // overlap with Live Notes / AI Assist / call controls).
      return clampFloaterPos(v);
    }
  } catch { /* ignore */ }
  return null;
}
function savePos(p) {
  try { sessionStorage.setItem(POS_KEY, JSON.stringify(p)); } catch { /* ignore */ }
}
function loadCollapsed() {
  try { return sessionStorage.getItem(COLLAPSED_KEY) === "1"; } catch { return false; }
}
function saveCollapsed(v) {
  try { sessionStorage.setItem(COLLAPSED_KEY, v ? "1" : "0"); } catch { /* ignore */ }
}

export default function FloatingLiveCallCoach() {
  const location = useLocation();
  const [active, setActive] = useState(false);
  const [lead, setLead] = useState(null);
  const [externalId, setExternalId] = useState(null);
  const [collapsed, setCollapsed] = useState(() => loadCollapsed());
  const [pos, setPos] = useState(() => loadPos() || defaultFloaterPos());
  // Local sentiment mirror so the collapsed pill can show a chip.
  const [sentiment, setSentiment] = useState("neutral");

  const dragRef = useRef({ active: false, startX: 0, startY: 0, startRight: 0, startBottom: 0 });

  // Drag handle (header) — same pattern as GlobalWebPhoneDock.
  const onDragStart = (e) => {
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    dragRef.current = {
      active: true,
      startX: clientX,
      startY: clientY,
      startRight: pos.right,
      startBottom: pos.bottom,
    };
    e.preventDefault();
  };
  useEffect(() => {
    const onMove = (e) => {
      if (!dragRef.current.active) return;
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      const dx = clientX - dragRef.current.startX;
      const dy = clientY - dragRef.current.startY;
      // Iter 346 — enforce the reserved bottom-dock strip while dragging.
      // Users can freely reposition the coach but cannot drop it on top
      // of the AI-FAB / NotificationBell / RC pill / Live Notes.
      const next = {
        right: Math.max(FLOATER_MIN_RIGHT, Math.min(window.innerWidth - 80, dragRef.current.startRight - dx)),
        bottom: Math.max(FLOATER_MIN_BOTTOM, Math.min(window.innerHeight - 80, dragRef.current.startBottom - dy)),
      };
      setPos(next);
    };
    const onUp = () => {
      if (!dragRef.current.active) return;
      dragRef.current.active = false;
      savePos(pos);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove);
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, [pos]);

  // Listen for call lifecycle. We deliberately do NOT mount audio
  // capture until we receive the start event — this guarantees we
  // can never accidentally grab the mic while the rep isn't on a
  // call, and we never collide with the SIP path inside RCWebPhone.
  useEffect(() => {
    const onStart = async (ev) => {
      const detail = ev?.detail || {};
      setExternalId(null);   // RC callId not in start event; populated on end if needed
      // Pull lead detail so the coach prompt knows the stage/specialty.
      const leadId = detail.lead_id || null;
      if (leadId) {
        try {
          const r = await axios.get(`${API}/leads/${leadId}`, { headers: authHeaders() });
          setLead(r?.data || { id: leadId });
        } catch {
          setLead({ id: leadId });
        }
      } else {
        setLead(null);
      }
      setActive(true);
    };
    const onEnd = (ev) => {
      const detail = ev?.detail || {};
      if (detail.external_id) setExternalId(detail.external_id);
      // Defer flipping `active` to false by one tick so LiveCoachPanel's
      // call-end persistence effect (which inspects active going
      // true→false) can run with externalId already populated.
      setTimeout(() => setActive(false), 0);
    };
    window.addEventListener("webphone:call-started", onStart);
    window.addEventListener("webphone:call-ended", onEnd);
    return () => {
      window.removeEventListener("webphone:call-started", onStart);
      window.removeEventListener("webphone:call-ended", onEnd);
    };
  }, []);

  const skipForRoute = SKIP_ROUTES.some(
    (r) => (location.pathname || "") === r || (location.pathname || "").startsWith(`${r}/`)
  );

  const toggleCollapsed = useCallback(() => {
    setCollapsed((v) => {
      saveCollapsed(!v);
      return !v;
    });
  }, []);

  // Render nothing when no call is active, or when an inline coach is
  // present on the current route.
  if (!active || skipForRoute) return null;

  // Collapsed = a small chip pinned to bottom-right above the dock.
  if (collapsed) {
    const dotColor = ({
      positive: "#10b981",
      neutral: "#94a3b8",
      negative: "#ef4444",
    })[sentiment] || "#94a3b8";
    return (
      <button
        data-testid="live-coach-floating-pill"
        onClick={toggleCollapsed}
        aria-label="Expand AI Live Coach"
        style={{
          position: "fixed", right: pos.right, bottom: pos.bottom, zIndex: 9100,
          background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
          color: "#fff", border: "0.5px solid rgba(255,255,255,0.12)",
          borderRadius: 999, padding: "6px 12px", cursor: "pointer",
          display: "inline-flex", alignItems: "center", gap: 7,
          fontSize: 11, fontWeight: 600, letterSpacing: "0.02em",
          boxShadow: "0 6px 22px rgba(15,23,42,0.35), 0 1px 3px rgba(0,0,0,0.2)",
        }}
        title="AI Live Coach — click to expand"
      >
        <Sparkles style={{ width: 12, height: 12, color: "#a5b4fc" }} />
        <span>Live coach</span>
        <span style={{ width: 7, height: 7, borderRadius: "50%", background: dotColor }} />
      </button>
    );
  }

  return (
    <div
      data-testid="live-coach-floating-panel"
      style={{
        position: "fixed", right: pos.right, bottom: pos.bottom, zIndex: 9100,
        width: 380, maxHeight: "78vh", overflowY: "auto",
        background: "#fff",
        borderRadius: 14,
        boxShadow: "0 14px 38px rgba(15,23,42,0.22), 0 1px 4px rgba(0,0,0,0.06)",
        border: "0.5px solid var(--border, #e2e8f0)",
        userSelect: dragRef.current.active ? "none" : "auto",
      }}
    >
      <div
        onMouseDown={onDragStart}
        onTouchStart={onDragStart}
        data-testid="live-coach-drag-handle"
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "8px 12px", borderBottom: "0.5px solid var(--border)",
          background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
          color: "#fff",
          borderTopLeftRadius: 14, borderTopRightRadius: 14,
          cursor: "move",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, fontWeight: 600 }}>
          <Move style={{ width: 12, height: 12, color: "rgba(255,255,255,0.55)" }} />
          <Sparkles style={{ width: 13, height: 13, color: "#a5b4fc" }} />
          AI Live Coach
          {lead?.name && (
            <span style={{ fontSize: 10, color: "rgba(255,255,255,0.6)", fontWeight: 500, marginLeft: 4 }}>
              · {lead.name}
            </span>
          )}
          <span style={{ fontSize: 10, color: "rgba(255,255,255,0.55)", fontWeight: 500, marginLeft: 6, display: "inline-flex", alignItems: "center", gap: 3 }}>
            <Phone style={{ width: 10, height: 10 }} /> on call
          </span>
        </div>
        <div style={{ display: "flex", gap: 4 }}>
          <button
            data-testid="live-coach-minimize"
            onClick={(e) => { e.stopPropagation(); toggleCollapsed(); }}
            aria-label="Minimize AI Live Coach"
            title="Minimize"
            style={{ background: "transparent", border: 0, cursor: "pointer", padding: 4, color: "rgba(255,255,255,0.75)" }}
          >
            <Minus style={{ width: 14, height: 14 }} />
          </button>
          <button
            data-testid="live-coach-close"
            onClick={(e) => { e.stopPropagation(); setActive(false); }}
            aria-label="Close AI Live Coach for this call"
            title="Close — coach will reappear on the next call"
            style={{ background: "transparent", border: 0, cursor: "pointer", padding: 4, color: "rgba(255,255,255,0.75)" }}
          >
            <X style={{ width: 14, height: 14 }} />
          </button>
        </div>
      </div>
      <div style={{ padding: 10 }}>
        <LiveCoachPanel
          active={active}
          lead={lead}
          externalId={externalId}
          onSentimentChange={(s) => setSentiment(s || "neutral")}
        />
      </div>
    </div>
  );
}
